import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  ACCEPT_LABELS,
  OnboardingStepBody,
  STEP_TITLE_FONT_CLASS,
  STEP_TITLES,
} from "./OnboardingStepContent";

const SILK_BG =
  "https://images.unsplash.com/photo-1707573090905-8465126a393e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxkYXJrJTIwcmVkJTIwYWJzdHJhY3QlMjBzaWxrJTIwdGV4dHVyZXxlbnwwfHx8fDE3Nzc1NDMyNjF8MA&ixlib=rb-4.1.0&q=85";

const STEPS = [1, 2, 3, 4, 5];

export default function OnboardingGateMobile({
  currentStep,
  acceptedSteps,
  setAccepted,
  nextStep,
  prevStep,
  finish,
  canAdvance,
}) {
  const isLast = currentStep === 5;

  return (
    <div className="relative min-h-[100dvh] w-full overflow-hidden bg-[#0a0a0a] text-[#f5f5f5]">
      <div
        className="pointer-events-none absolute inset-0 z-[1] opacity-[0.22]"
        style={{
          backgroundImage: `url(${SILK_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(2px) saturate(85%)",
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 z-[2]"
        style={{
          background:
            "radial-gradient(ellipse at top, rgba(139,30,63,0.14), transparent 58%), linear-gradient(180deg, rgba(10,10,10,0.5), rgba(10,10,10,0.94))",
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 z-[3] mix-blend-overlay opacity-25"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.35'/></svg>\")",
        }}
        aria-hidden
      />

      <div className="relative z-10 flex min-h-[100dvh] flex-col pb-[env(safe-area-inset-bottom)]">
        <header className="flex shrink-0 items-center justify-between gap-3 px-5 pt-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[rgba(245,245,245,0.45)]">
            Onboarding
          </p>
          <p
            className="font-mono text-xs tabular-nums text-[rgba(245,245,245,0.75)]"
            aria-live="polite"
          >
            {currentStep} / 5
          </p>
        </header>

        <div className="mx-4 mt-4 min-h-0 shrink-0 overflow-hidden px-0 pt-0 h-[min(520px,calc(100dvh-13.5rem))] min-h-[260px]">
          <div
            className="flex w-full flex-col transition-transform duration-500 ease-out motion-reduce:transition-none"
            style={{
              transform: `translateY(-${((currentStep - 1) / STEPS.length) * 100}%)`,
            }}
          >
            {STEPS.map((step) => (
              <div
                key={step}
                className="panel-slide flex h-[min(520px,calc(100dvh-13.5rem))] min-h-[260px] w-full shrink-0 flex-col"
                aria-hidden={step !== currentStep}
              >
                <div className="zx-card zx-card-static zx-glass flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-[rgba(255,255,255,0.1)]">
                  <div className="zx-no-scrollbar min-h-0 flex-1 overflow-y-auto px-5 pb-4 pt-6">
                    <h2
                      className={cn(
                        STEP_TITLE_FONT_CLASS,
                        "text-xl font-normal leading-snug tracking-tight text-[#f5f5f5]"
                      )}
                    >
                      {STEP_TITLES[step]}
                    </h2>
                    <div
                      key={step === currentStep ? `active-${currentStep}` : `stash-${step}`}
                      className={[
                        "mt-5",
                        step === currentStep
                          ? "zx-page-enter motion-reduce:animate-none"
                          : "",
                      ].join(" ")}
                    >
                      <OnboardingStepBody step={step} layout="mobile" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <footer className="shrink-0 border-t border-[rgba(255,255,255,0.08)] bg-[rgba(10,10,10,0.85)] px-4 py-4 backdrop-blur-xl">
          <div className="mx-auto flex max-w-lg flex-col gap-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id={`accept-mobile-${currentStep}`}
                checked={acceptedSteps[currentStep]}
                onCheckedChange={(v) => setAccepted(currentStep, v === true)}
                className="mt-0.5 border-[rgba(255,255,255,0.35)] data-[state=checked]:bg-[var(--zx-accent)] data-[state=checked]:border-[var(--zx-accent)]"
              />
              <Label
                htmlFor={`accept-mobile-${currentStep}`}
                className="cursor-pointer text-left font-mono text-[11px] leading-snug text-[rgba(245,245,245,0.75)]"
              >
                {ACCEPT_LABELS[currentStep]}
              </Label>
            </div>

            <div className="flex flex-col gap-2">
              {currentStep > 1 ? (
                <button type="button" className="zx-btn-ghost" onClick={prevStep}>
                  Atrás
                </button>
              ) : null}
              {isLast ? (
                <button
                  type="button"
                  className="zx-btn-primary"
                  disabled={!acceptedSteps[5]}
                  onClick={finish}
                >
                  Entrar en La Lista
                </button>
              ) : (
                <button
                  type="button"
                  className="zx-btn-primary"
                  disabled={!canAdvance}
                  onClick={nextStep}
                >
                  Continuar
                </button>
              )}
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
